import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ShellSidebar } from "./ShellSidebar";
import { ShellHeader } from "./ShellHeader";
import { Breadcrumbs } from "./Breadcrumbs";
import { BreadcrumbLabelsProvider } from "./BreadcrumbLabelsProvider";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useI18n } from "@/lib/i18n";
import { isFocusRoute, useSidebarCollapsed } from "./useSidebarCollapsed";

/** Responsive breakpoint: below this, sidebar becomes a sheet/drawer. */
const BREAKPOINT_SIDEBAR_COLLAPSIBLE = 768;

function useScreenWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined"
      ? window.innerWidth
      : BREAKPOINT_SIDEBAR_COLLAPSIBLE,
  );

  useEffect(() => {
    function handleResize() {
      setWidth(window.innerWidth);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
}

/**
 * Shell-aware layout with responsive sidebar. A page names the ids of its
 * path in the breadcrumbs (QA-07: the project's name, not its id prefix).
 */
export function ShellLayout() {
  return (
    <BreadcrumbLabelsProvider>
      <ShellLayoutFrame />
    </BreadcrumbLabelsProvider>
  );
}

function ShellLayoutFrame() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const screenWidth = useScreenWidth();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Responsive behavior:
  // >= 768px: sidebar visible, full width or collapsed to icons (⌘B / Ctrl+B);
  //           it collapses on its own on work surfaces (focus mode)
  // < 768px: sidebar hidden, accessible via sheet/drawer at full width
  const isMobile = screenWidth < BREAKPOINT_SIDEBAR_COLLAPSIBLE;
  const { collapsed, toggleCollapsed } = useSidebarCollapsed(
    !isMobile,
    !isMobile && isFocusRoute(pathname),
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Skip-to-content link — a11y */}
      <a
        href="#main-content"
        className="focus:bg-brand-primary sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:rounded-md focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-lg"
      >
        {t("common.skipToContent")}
      </a>

      {/* Desktop / Tablet sidebar — the operator chooses full or icon-only */}
      {!isMobile && (
        <ShellSidebar
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
        />
      )}

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <ShellHeader>
          {/* Mobile hamburger */}
          {isMobile && (
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="mr-2">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">{t("common.toggleMenu")}</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <ShellSidebar />
              </SheetContent>
            </Sheet>
          )}
        </ShellHeader>
        <div className="min-w-0 border-b border-[var(--border)] px-3 py-2 sm:px-7">
          <Breadcrumbs />
        </div>
        <main
          id="main-content"
          className="min-w-0 flex-1 overflow-y-auto bg-[var(--bg)] p-3 sm:p-7"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
